import React, { useEffect, useMemo, useState } from 'react'
import type { GetServerSideProps } from 'next'
import ProductImageCarousal from '@/tabs/product/ProductImageCarousal'
import ProductInfoSections from '@/tabs/product/ProductDetails' // ⬅️ renamed inner component export
import productApis from '@/apis/product'
import WhyShopSection from '@/tabs/product/WhyShopSection'
import CartCounter from '@/components/common/cart-counter'
import useIsMobile from '@/hooks/useIsMobile'
import { useSelector } from 'react-redux'
import { type RootState } from '@/store'
import Breadcrumb from '@/routes/store/bread-crum'
import ProductImageSliderMobile from '@/tabs/product/ProductImageSlider'
import Spinner from '@/components/Spinner'
import Placeholder from '@/components/PlaceHolder'
import Icons from '@/components/icons'
import Head from 'next/head'
import { getPDPSeoDetails } from '@/utils/getSeoDetails'
import { getSeoProductUrl, isGuestUser } from '@/lib/utilities/Utils'
import { sortVariantsByStock } from '@/utils/getInstockProductsFirst'
import WebToAppPopup from '@/components/common/web-to-app-popup'
import { useRouter } from 'next/router'

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { slug } = context.params as { slug: string }
  const { sid } = context.query as { sid?: string }

  const productId = slug?.split('-').pop() ?? ''
  const storeId = sid ?? ''

  const slugify = (text: string): string =>
    text
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')

  if (productId === '') {
    return { notFound: true }
  }

  try {
    const response = await productApis.productDetail(productId, storeId)
    const product = response?.data ?? response

    if (product === null || product === undefined) {
      return { notFound: true }
    }

    const productData = product?.data

    const productName =
      productData?.productsWithMeasurements?.[0]?.name ??
      productData?.name ??
      ''

    if (productName !== '') {
      const expectedSlug = `${slugify(productName)}-${productId}`

      if (slug !== expectedSlug) {
        return {
          redirect: {
            destination: `/products/${expectedSlug}${storeId !== '' ? `?sid=${storeId}` : ''}`,
            permanent: false
          }
        }
      }
    }

    return {
      props: {
        serverProduct: JSON.parse(JSON.stringify(productData)),
        productId,
        storeId
      }
    }
  } catch (error) {
    return { notFound: true }
  }
}

declare global {
  interface Window {
    dataLayer: any[]
  }
}

export {}

interface Variant {
  retailerProductId: number
  libraryProductId: number
  name: string
  imageUrls: string[]
  inStock: boolean
  measurementDisplayText: string
  approxGrammage: string
  mrp: number | string
  sellingPrice: number | string
  displayTag?: string
  offerPercent: number
  cartQuantity: number
  quantity: number
  store?: {
    name: string
    id: number
  }
}

interface Product {
  name: string
  retailerProductId: number
  imageUrls?: string[]
  images?: string[]
  description?: string
  ingredients?: string
  other_product_info?: []
  specifications?: Record<string, string> | null
  store?: {
    name: string
    id: number
  }
  storeName?: string
  store_id: number
  locality: string
  city: string
  country: string
  storeTypeName: string
  shopUrl: string
  hasSeoData: boolean
  category: string []
  libraryProductId?: number
  brand?: string
}

interface Props {
  productId: string
  storeId: string
  serverProduct: any
}

export default function ProductDetailsPage ({
  productId: initialProductId,
  storeId: initialStoreId,
  serverProduct
}: Props): JSX.Element {
  const router = useRouter()
  const slug = router.query.slug as string
  const actualProductId = slug?.split('-').pop()
  const [storeId, setStoreId] = useState(serverProduct?.store_id)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [showPopup, setShowPopup] = useState(false)
  const [variants, setVariants] = useState<Variant[]>(
    () =>
      sortVariantsByStock(
        serverProduct?.productsWithMeasurements ?? []
      )
  )
  const [product, setProduct] = useState<Product | null>(serverProduct?.productsWithMeasurements?.[0] ?? null)
  const [data, setData] = useState<Product | null>(serverProduct)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showStoreVariants, setShowStoreVariants] = useState(false)
  const [selectedVariantId, setSelectedVariantId] = useState<number | null>(null)

  const seo = getPDPSeoDetails(product, serverProduct?.category?.[1])
  const { pathName } = getSeoProductUrl(actualProductId, product?.name, product?.store?.id)
  const isMobile = useIsMobile()
  const loggedInCartItems = useSelector((state: RootState) => state?.commonData?.cartItems)
  const guestCartItems = useSelector((state: RootState) => state?.commonData?.cartItems)

  const cartProduct = isGuestUser()
    ? guestCartItems?.[product?.retailerProductId ?? 0]
    : loggedInCartItems?.[product?.retailerProductId ?? 0]
  const cartQuantityForGuestUser = cartProduct?.quantity ?? 0
  const isProductInvalid =
    (product == null) ||
    (variants == null) ||
    variants?.length === 0

  const quantity =
    cartProduct !== null && cartProduct?.storeId === product?.store?.id
      ? cartProduct?.quantity ?? 0
      : 0

  const loadProductData = (): void => {
    setLoading(true)
    setError(null)

    productApis.productDetail(initialProductId, initialStoreId)
      .then((res: any) => {
        const data = res?.data?.data
        const firstVariant: Variant | undefined = data?.productsWithMeasurements?.[0]

        setProduct(data?.productsWithMeasurements?.[0] ?? null)
        setVariants(sortVariantsByStock(data?.productsWithMeasurements))
        setData(data)
        setSelectedVariantId(firstVariant?.libraryProductId ?? null)
        setStoreId(data?.variants !== null ? data?.storeId : data?.store_id)
        setLoading(false)
      })
      .catch(() => {
        setError('Something went wrong')
        setLoading(false)
      })
  }

  useEffect(() => {
    const hasSeenPopup = sessionStorage.getItem('hasSeenPopup')

    if (hasSeenPopup === 'true') return

    const timer = setTimeout(() => {
      setShowPopup(true)
      sessionStorage.setItem('hasSeenPopup', 'true')
    }, 5000)
    return () => {
      clearTimeout(timer)
    }
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return

    window.dataLayer = Array.isArray(window.dataLayer)
      ? window.dataLayer
      : []

    const eventData = {
      page_url: window.location.href,
      product_id: product?.retailerProductId,
      product_name: product?.name,
      store_id: storeId
    }

    window.dataLayer.push({
      event: 'product_detail_page',
      ...eventData
    })

    ;(window as any).gtag?.('event', 'product_detail_page', eventData)

    window.dataLayer.push({
      event: 'view_item',
      ...eventData
    })

    ;(window as any).gtag?.('event', 'view_item', {
      currency: 'INR',
      value: selectedVariant?.sellingPrice,
      items: [
        {
          item_id: product?.retailerProductId,
          item_name: product?.name
        }
      ]
    })
  }, [product?.retailerProductId, storeId])

  const handleAddProduct = (): void => {
    if (selectedVariant == null) return
    if (isMobile) {
      //
    } else {
      setShowStoreVariants(true)
    }
  }
  const selectedVariant = useMemo(
    () => variants?.find(v => v?.libraryProductId === selectedVariantId) ?? variants?.[0],
    [variants, selectedVariantId]
  )

  const mrp = Number(selectedVariant?.mrp ?? 0)
  const sell = Number(selectedVariant?.sellingPrice ?? 0)
  const schemaProductName = ((selectedVariant?.measurementDisplayText) !== '')
    ? `${selectedVariant?.name ?? product?.name}, ${selectedVariant?.measurementDisplayText}`
    : (selectedVariant?.name ?? product?.name)
  const cleanStoreType = data?.storeTypeName?.replace(/shops?/gi, '').trim().replace(/\s+/g, ' ')

  if (loading) {
    return (
      <div className="flex w-full justify-center items-center min-h-[320px]">
        <Spinner bgColor="pink"/>
      </div>
    )
  } else if (error !== null) {
    return (
      <div className="flex w-full justify-center items-center min-h-[320px]">
        <Placeholder
          handleRetry={loadProductData}
          imageSrc="/images/error-placeholder.png"
          heading="Oh no! Something broke"
        />
      </div>
    )
  } else if (isProductInvalid) {
    return (
      <div className="flex w-full justify-center items-center min-h-[320px]">
        <Placeholder
          // handleRetry={loadProductData}
          imageSrc="/images/error-placeholder.png"
          heading="Product not available on this store"
        />
      </div>
    )
  }
  return (
    <>
      <Head>
        <title>
          {seo?.title ?? ''}
        </title>

        <meta
          name="description"
          content={seo?.description ?? ''}
        />
        <link
          rel="canonical"
          href={`https://${window.location.host}${pathName}`}
        />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org/',
            '@type': 'Product',
            name: schemaProductName,
            image: product?.images ?? product?.imageUrls ?? [],
            offers: {
              '@type': 'Offer',
              priceCurrency: 'INR',
              price: String((sell !== 0) ? sell : mrp),
              availability: selectedVariant?.inStock
                ? 'https://schema.org/InStock'
                : 'https://schema.org/OutOfStock',
              itemCondition: 'https://schema.org/NewCondition'
            },
            category:
              (cleanStoreType != null && cleanStoreType !== '')
                ? cleanStoreType
                : (data?.category?.[0] != null && data?.category?.[0] !== '')
                    ? data?.category?.[0]
                    : '',
            brand: {
              '@type': 'Brand',
              name: product?.brand ?? 'Unbranded'
            }
          })}
        </script>
      </Head>
      <div className={['relative w-full bg-white lg:px-20 px-4', isMobile ? 'pb-24' : 'pb-10'].join(' ')}>
        <Breadcrumb
          country="india"
          city={data?.city}
          locality={data?.locality}
          storeType={data?.storeTypeName}
          shopUrl={data?.shopUrl}
          shopName={data?.storeName}
          hasSeoData={true}
          cityTitle={data?.city}
          localityTitle={data?.locality}
          countryTitle={data?.country}
          storeTypeTitle={data?.storeTypeName}
        />
        <div
          className="relative w-full md:w-[85%] md:max-w-[1366px] md:mx-auto md:rounded-xl md:p-8 mx-auto justify-center">
          <div className="grid grid-cols-1 lg:grid-cols-[60%_1fr] lg:items-start gap-8">
            {/* Left: Images */}
            <div className='w-[95%]'>
              {isMobile
                ? (
                  <ProductImageSliderMobile
                    images={(product?.images ?? product?.imageUrls) ?? []}
                    imageAlt={product?.name}
                  />
                  )
                : (
                  <ProductImageCarousal
                    images={(product?.images ?? product?.imageUrls) ?? []}
                    imageAlt={product?.name}
                    selectedVariant={selectedVariant}
                  />
                  )}
            </div>

            {/* Right: Title + Pricing + Variants + CTA */}
            <div className="w-[85%] max-w-[520px]">
              <h1 className="text-xl font-extrabold leading-snug mb-1">
                {selectedVariant?.name ?? product?.name}
              </h1>

              {selectedVariant?.measurementDisplayText !== '' && (
                <div className="text-sm text-gray-500 mb-4">
                  {selectedVariant?.measurementDisplayText}
                </div>
              )}

              {/* Price block */}
              <div className="flex  items-center gap-3 mb-2">
                <div className="text-lg font-bold">₹ {sell ?? mrp}</div>
                {selectedVariant?.offerPercent !== null && selectedVariant?.offerPercent > 0 && (
                  <>
                    <div className="text-gray-500 line-through">₹ {mrp}</div>
                    <span
                      className="inline-flex items-center rounded-lg px-2 py-1 text-xs font-semibold bg-[#B8238E] text-white">
                    {selectedVariant?.offerPercent}% off
                  </span>
                  </>
                )}
              </div>

              {/* Available in */}
              {variants.length > 0 && (
                <div className="mt-5">
                  <div className="text-sm text-gray-600 mb-2">Available in:</div>
                  <div className="flex flex-wrap gap-2">
                    {variants.map(v => {
                      const active = v.libraryProductId === selectedVariant?.libraryProductId
                      return (
                        <button
                          key={v.libraryProductId}
                          type="button"
                          onClick={() => {
                            setSelectedVariantId(v.libraryProductId)
                          }}
                          className={[
                            'h-12 min-w-[72px] rounded-lg border px-3 text-sm font-semibold transition-colors',

                            !v?.inStock
                              ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed opacity-60'
                              : active
                                ? 'bg-[#FFF2FB] text-[#9D0B75] border-[#9D0B75]'
                                : 'border-gray-300 text-gray-700 hover:border-gray-400 hover:bg-gray-50'

                          ].join(' ')}
                          disabled={!v?.inStock}
                          aria-pressed={active}
                        >
                          <div>
                            {v.measurementDisplayText}
                          </div>
                          <div>
                            {v?.approxGrammage}
                          </div>
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Shopping from */}
              {storeId !== '' && (
                <div className="mt-5">
                  <label
                    htmlFor="shopping-store"
                    className="block text-sm text-gray-600 mb-2"
                  >
                    Shopping from:
                  </label>
                  <div
                    className="relative flex flex-row items-center w-full h-10 rounded-lg border border-gray-300 px-3 text-md font-bold bg-white cursor-not-allowed"
                  >
                    <Icons name="cash-and-carry"/>
                    <select
                      id="shopping-store"
                      name="shopping-store"
                      className="w-full bg-transparent outline-none px-2 cursor-not-allowed"
                      disabled
                      value={product?.store?.name ?? product?.storeName ?? 'Unknown Store'}
                    >
                      <option className='truncate'>
                        {product?.store?.name ?? product?.storeName ?? 'Unknown Store'}
                      </option>
                    </select>
                  </div>
                </div>
              )}

              {/* Add to Cart — desktop only, mobile version lives in the fixed bottom bar */}
              {!isMobile && (
                <div className="mt-6">
                  {storeId !== null
                    ? (
                      <CartCounter
                        quantity={quantity ?? 0}
                        product={selectedVariant}
                        inStock={selectedVariant?.inStock}
                        fromProductDetailsPage
                      />
                      )
                    : (
                      <>
                        <button
                          type="button"
                          onClick={handleAddProduct}
                          disabled={!selectedVariant?.inStock}
                          className={[
                            'w-full h-11 rounded-lg text-white font-semibold',
                            selectedVariant?.inStock
                              ? 'bg-gradient-to-r from-[#D81DA4] to-[#B8238E] border border-[#9D0B75] hover:opacity-90'
                              : 'bg-gray-300 cursor-not-allowed'
                          ].join(' ')}
                        >
                          {selectedVariant?.inStock ? 'Add To Cart' : 'Out of Stock'}
                        </button>

                        {!selectedVariant?.inStock && (
                          <div className="mt-2 text-sm text-gray-600">
                            *This product is not available in stock
                          </div>
                        )}
                      </>
                      )}
                </div>
              )}
            </div>
          </div>

          <div className="lg:px-32">
            {/* Details sections */}
            {(product !== null && ((product?.description !== '') || (product?.ingredients != null) || (product.other_product_info != null)) && (
              <div className="mt-10">
                <ProductInfoSections details={product}/>
              </div>
            ))}

            {/* Why Shop */}
            <div className="mt-10">
              <WhyShopSection/>
            </div>
          </div>
        </div>
        <WebToAppPopup
          isOpen={showPopup}
          onClose={() => {
            setShowPopup(false)
          }}
        />
      </div>

      {isMobile && (
        <div
          className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-[#E0E0E0] px-4 py-2 flex items-center justify-between gap-4 shadow-[0_-4px_24px_0_#00000099]">
          <div>
            <div className="text-[14px] font-bold">₹ {sell ?? mrp}</div>
            {selectedVariant?.offerPercent !== null && selectedVariant?.offerPercent > 0 && (
              <div className="text-[12px] text-end text-gray-500 line-through">₹ {mrp}</div>
            )}
          </div>

          <div className="flex flex-1 items-center gap-3 flex-shrink-0">
            {storeId !== null
              ? (
                <>
                  <div className={`${cartQuantityForGuestUser > 0 ? 'w-[100px]' : 'flex-1'} `}>
                    <CartCounter
                      quantity={quantity ?? 0}
                      product={selectedVariant}
                      inStock={selectedVariant?.inStock}
                      fromProductDetailsPage={true}
                    />
                  </div>

                  {cartQuantityForGuestUser > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        void router.push('/cart')
                      }}
                      className="py-[12px] text-[15px] font-black px-5 rounded-lg text-white  bg-[#B8238E] border border-[#9D0B75] hover:opacity-90 whitespace-nowrap flex-1"
                    >
                      Go to Cart
                    </button>
                  )}
                </>
                )
              : (
                <div className="w-40">
                  <button
                    type="button"
                    onClick={handleAddProduct}
                    disabled={!selectedVariant?.inStock}
                    className={[
                      'w-full h-11 rounded-lg text-white font-black flex-1',
                      selectedVariant?.inStock
                        ? 'bg-[#B8238E] text-white font-black border border-[#9D0B75] hover:opacity-90'
                        : 'bg-gray-300 cursor-not-allowed'
                    ].join(' ')}
                  >
                    {selectedVariant?.inStock ? 'Add To Cart' : 'Out of Stock'}
                  </button>
                </div>
                )}
          </div>
        </div>
      )}
    </>
  )
}
